import { useEffect, useState } from "react";
import useOKTAUserStore, {
  OKTARegistrationStatus,
  OKTAUserTypeEnum,
  UserStore,
} from "@store/useOKTAUserStore";
import { isEmpty } from "lodash-es";

/**
 * Hook to get the user info flags and the userInfo object from the store.
 * Notes:
 * As userInfo object use in client side, it will cause hydration mismatch issue if we directly use it in the component.
 * So, we use this hook to get the userInfo object from the store and set the various user type info flags.
 * @returns Various user type info flags and the userInfo object from the store.
 */
export const useUserInfo = () => {
  const userInfo = useOKTAUserStore((state: UserStore) => state.userInfo);

  const [isAnonymous, setIsAnonymous] = useState<boolean>(true);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [isSubscriber, setIsSubscriber] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userType, setUserType] = useState<OKTAUserTypeEnum>(
    OKTAUserTypeEnum.ANONYMOUS
  );
  const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
  const [displayName, setDisplayName] = useState<string | undefined>();
  const [loginId, setLoginId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!userInfo) return;

    setIsAnonymous(userInfo.usertype === OKTAUserTypeEnum.ANONYMOUS);
    setIsRegistered(userInfo.usertype === OKTAUserTypeEnum.REGISTERED);
    setIsSubscriber(userInfo.usertype === OKTAUserTypeEnum.SUBSCRIBER);
    setIsLoggedIn(!isEmpty(userInfo.loginid));
    setUserType(userInfo.usertype || OKTAUserTypeEnum.ANONYMOUS);
    setIsVerified(userInfo.reguserstatus === OKTARegistrationStatus.VERIFIED);
    setVisitorId(userInfo.mysphw);
    setLoginId(userInfo.loginid);

    const name = userInfo.display_name || userInfo.loginid;
    setDisplayName(name);
  }, [userInfo]);

  return {
    isSubscriber,
    isAnonymous,
    isRegistered,
    isVerified,
    isUnverified: !isVerified,
    isLoggedIn,
    userType,
    visitorId,
    displayName,
    loginId,
    userInfo,
  };
};
